import React from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'nucleus-text';
import ConnectionError from '@images/ConnectionError';
import getConfig from 'next/config';
import ErrorContent from './ErrorContent';
import EmptyContentState from './EmptyContentState';

const { publicRuntimeConfig } = getConfig();

const useStyles = () => {
  return {
    container: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    subtitle: {
      width: 440,
      marginBottom: 32
    }
  };
};

/**
 * Component for showing an error state
 */
const ErrorState = ({
  title,
  image,
  subtitle,
  hideTitle,
  hideSubtitle,
  hideAdditionalInfo,
  testID,
  errorFields,
  cventSupportUrl,
  imageAlt
}) => {
  const styling = useStyles();
  const { translate: t } = useTranslate();
  const titleControlled = hideTitle
    ? null
    : title || t('unexpected_error_title');
  const subtitleControlled = hideSubtitle
    ? null
    : subtitle || t('unexpected_error_notified');

  return (
    <div css={styling.container}>
      <EmptyContentState
        image={image}
        title={titleControlled}
        subtitle={
          subtitleControlled ? (
            <span css={styling.subtitle}>{subtitleControlled}</span>
          ) : null
        }
        testID={testID}
        imageAlt={imageAlt}
        hasLargeTitle
        hasCustomParagraphWidth
      />
      {!hideAdditionalInfo && (
        <ErrorContent
          {...errorFields}
          testID={'error-info'}
          cventSupportUrl={cventSupportUrl}
        />
      )}
    </div>
  );
};

ErrorState.propTypes = {
  /** Illustration */
  image: PropTypes.node,
  /** Image alt */
  imageAlt: PropTypes.string,
  /** Title */
  title: PropTypes.string,
  /** Error Message */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Hide the title */
  hideTitle: PropTypes.bool,
  /** Hide the subtitle */
  hideSubtitle: PropTypes.bool,
  /** Hide extra info panel */
  hideAdditionalInfo: PropTypes.bool,
  /** testId */
  testID: PropTypes.string,
  /** errorFields for displaying on error screen */
  errorFields: PropTypes.shape({
    requestId: PropTypes.string,
    pageLoadId: PropTypes.string
  }),
  /** cventSupportUrl for link on error screen */
  cventSupportUrl: PropTypes.string
};

ErrorState.defaultProps = {
  hideTitle: false,
  testID: 'error-state',
  image: <ConnectionError />,
  cventSupportUrl: publicRuntimeConfig.CVENT_SUPPORT_URL
};

export default ErrorState;
